import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'

const DEFAULT_WIDTH = 244
const MIN_WIDTH = 200
const MAX_WIDTH = 420
const COLLAPSE_THRESHOLD = 140
type SidebarSize = { width: number; collapsed: boolean }
const clamp = (width: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width))

function readSize(): SidebarSize {
  try {
    const raw = localStorage.getItem('imed.sidebar-width')
    const width = Number(raw)
    const savedCollapsed = localStorage.getItem('imed.sidebar-collapsed')
    return { width: raw && Number.isFinite(width) ? clamp(width) : DEFAULT_WIDTH, collapsed: savedCollapsed === null ? true : savedCollapsed === 'true' }
  } catch { return { width: DEFAULT_WIDTH, collapsed: true } }
}

export function useResizableSidebar() {
  const [size, setSize] = useState(readSize)
  const current = useRef(size)
  const drag = useRef<{ x: number; initial: SidebarSize; pointerId: number } | null>(null)
  const update = (next: SidebarSize, persist = false) => {
    current.current = next
    setSize(next)
    if (persist) {
      try {
        localStorage.setItem('imed.sidebar-width', String(next.width))
        localStorage.setItem('imed.sidebar-collapsed', String(next.collapsed))
      } catch { /* Resizing remains usable when storage is unavailable. */ }
    }
  }
  const finish = (cancel = false) => {
    if (!drag.current) return
    const initial = drag.current.initial
    drag.current = null
    document.body.classList.remove('sidebar-resizing')
    update(cancel ? initial : current.current, !cancel)
  }
  useEffect(() => {
    const cancel = () => {
      if (!drag.current) return
      current.current = drag.current.initial
      setSize(current.current)
      drag.current = null
      document.body.classList.remove('sidebar-resizing')
    }
    window.addEventListener('blur', cancel)
    return () => { window.removeEventListener('blur', cancel); document.body.classList.remove('sidebar-resizing') }
  }, [])

  const toggle = () => update({ ...current.current, collapsed: !current.current.collapsed }, true)
  const reset = () => update({ width: DEFAULT_WIDTH, collapsed: false }, true)
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || window.innerWidth <= 900) return
    event.preventDefault()
    event.currentTarget.focus({ preventScroll: true })
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { x: event.clientX, initial: { ...current.current }, pointerId: event.pointerId }
    document.body.classList.add('sidebar-resizing')
  }
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const active = drag.current
    if (!active || active.pointerId !== event.pointerId) return
    const requested = (active.initial.collapsed ? 0 : active.initial.width) + event.clientX - active.x
    update(requested < COLLAPSE_THRESHOLD ? { width: active.initial.width, collapsed: true } : { width: clamp(requested), collapsed: false })
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', ' ', 'Escape'].includes(event.key)) return
    event.preventDefault()
    if (event.key === 'Escape') { finish(true); return }
    if (event.key === 'Enter' || event.key === ' ') { toggle(); return }
    if (event.key === 'Home') { update({ ...current.current, collapsed: true }, true); return }
    if (event.key === 'End') { update({ width: MAX_WIDTH, collapsed: false }, true); return }
    if (current.current.collapsed) { if (event.key === 'ArrowRight') update({ ...current.current, collapsed: false }, true); return }
    const requested = current.current.width + (event.key === 'ArrowLeft' ? -20 : 20)
    update({ width: clamp(requested), collapsed: requested < MIN_WIDTH }, true)
  }
  return { collapsed: size.collapsed, effectiveWidth: size.collapsed ? 0 : size.width, toggle, dividerProps: {
    role: 'separator', tabIndex: 0, 'aria-label': 'Resize navigation sidebar', 'aria-orientation': 'vertical' as const,
    'aria-valuemin': 0, 'aria-valuemax': MAX_WIDTH, 'aria-valuenow': size.collapsed ? 0 : size.width,
    'aria-valuetext': size.collapsed ? 'Collapsed' : `${size.width} pixels`,
    title: 'Drag to resize or collapse · Double-click to reset · Arrow keys to resize',
    onPointerDown, onPointerMove, onPointerUp: () => finish(), onPointerCancel: () => finish(true),
    onLostPointerCapture: () => finish(), onKeyDown, onDoubleClick: reset,
  } }
}
